import * as turf from '@turf/turf';
import { lv95ToWgs84ForOneCoordinate } from '../map/coordinateTransforms';
import type { Lv95Coordinate, TurfPolygon } from './cadastralShared';
import { GRID_CONTAINMENT_INNER_BUFFER_METERS, GRID_CONTAINMENT_TOLERANCE_AREA } from 'constants/grid';

export type Wgs84Coordinate = { lng: number; lat: number };

export const getCombinedAnalysisAreasCenter = (areas: Lv95Coordinate[][]): Lv95Coordinate | null => {
  const points = areas
    .filter((polygon) => Array.isArray(polygon) && polygon.length >= 3)
    .flat()
    .filter((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y));

  if (points.length === 0) {
    return null;
  }

  const bounds = points.reduce(
    (acc, point) => ({
      minX: Math.min(acc.minX, point.x),
      maxX: Math.max(acc.maxX, point.x),
      minY: Math.min(acc.minY, point.y),
      maxY: Math.max(acc.maxY, point.y),
    }),
    {
      minX: points[0].x,
      maxX: points[0].x,
      minY: points[0].y,
      maxY: points[0].y,
    }
  );

  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
};

export const buildTurfPolygonFromLatLngs = (latLngs: Wgs84Coordinate[] | undefined | null): TurfPolygon | null => {
  if (!latLngs?.length) {
    return null;
  }
  const formatted = latLngs.map((coord) => [coord.lng, coord.lat]);
  formatted.push(formatted[0]);
  return turf.polygon([formatted]);
};

export const buildTurfPolygonFromLv95 = (lv95Coordinates: Lv95Coordinate[] | undefined | null): TurfPolygon | null => {
  if (!lv95Coordinates?.length) {
    return null;
  }
  return buildTurfPolygonFromLatLngs(lv95Coordinates.map((coord) => lv95ToWgs84ForOneCoordinate(coord.x, coord.y)));
};

export const buildProjectLocationRadiusPolygon = (
  coordinates: { lat: number; lng: number } | null | undefined,
  radiusKilometers: number
) => {
  const lat = Number(coordinates?.lat);
  const lng = Number(coordinates?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return turf.circle([lng, lat], radiusKilometers, { steps: 64, units: 'kilometers' });
};

export const analysisAreaIntersectsProjectRadius = (
  analysisPolygon: TurfPolygon | null,
  projectRadiusPolygon: TurfPolygon | null
) => {
  if (!analysisPolygon || !projectRadiusPolygon) {
    return true;
  }
  try {
    return turf.booleanIntersects(analysisPolygon, projectRadiusPolygon);
  } catch {
    return false;
  }
};

export const requiresProjectRadiusValidation = (polygonType: string | null, analysisAreaType: string | undefined) =>
  polygonType === 'analysisArea' ||
  (polygonType === 'legalBorder' && analysisAreaType === 'asTheLegalBorder') ||
  (polygonType === 'buildableBorder' && analysisAreaType === 'asTheBuildableBorder');

export const isPolygonContained = (outerPolygon: TurfPolygon | null, innerPolygon: TurfPolygon | null) => {
  if (!outerPolygon || !innerPolygon) {
    return true;
  }
  try {
    const shrunkenInnerPolygon = turf.buffer(innerPolygon, -GRID_CONTAINMENT_INNER_BUFFER_METERS, {
      units: 'meters',
      steps: 8,
    });
    const candidateInnerPolygon =
      shrunkenInnerPolygon && turf.area(shrunkenInnerPolygon) > 0 ? shrunkenInnerPolygon : innerPolygon;
    const innerArea = turf.area(candidateInnerPolygon);
    const tolerance = Math.max(GRID_CONTAINMENT_TOLERANCE_AREA, innerArea * 1e-5);
    const diff = turf.difference(turf.featureCollection([candidateInnerPolygon, outerPolygon]));
    return !diff || turf.area(diff) <= tolerance;
  } catch {
    return false;
  }
};
